import type { PrismaClient } from '@prisma/client';
import { Router } from 'express';
import type { Config } from '../../core/config.js';
import { asyncHandler } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

interface Hit {
  type: string;
  id: number;
  label: string;
  sub?: string;
  url: string;
}

const SALES_BASE: Record<string, [string, string]> = {
  invoice: ['/sales/invoices', 'Invoice'],
  quotation: ['/sales/quotations', 'Quotation'],
  proforma: ['/sales/proformas', 'Proforma'],
  creditnote: ['/sales/credit-notes', 'Credit note'],
};

/** Global search (Ctrl+K): each group is searched only if the user may view it, and own-data scope applies. */
export function createSearchRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/search', authenticate({ db, config }));

  router.get(
    '/search',
    asyncHandler(async (req, res) => {
      const auth = req.auth!;
      const can = (p: string) => permissionsGranted(auth.permissions, p);
      const q = String(req.query.q ?? '')
        .trim()
        .slice(0, 80);
      if (q.length < 2) return void res.json({ items: [] });
      const own = auth.scope === 'own';
      const companyId = auth.companyId;
      const hits: Hit[] = [];
      const take = 5;
      const like = { contains: q };

      if (can('sales.client.view')) {
        const rows = await db.party.findMany({
          where: {
            companyId,
            OR: [{ isClient: true }, { isProspect: true }],
            ...(own ? { ownerId: auth.userId } : {}),
            AND: [
              {
                OR: [
                  { name: like },
                  { tin: like },
                  { contacts: { some: { OR: [{ email: like }, { phone: like }] } } },
                ],
              },
            ],
          },
          take,
          orderBy: { name: 'asc' },
        });
        for (const p of rows)
          hits.push({
            type: 'Client',
            id: p.id,
            label: p.name,
            sub: p.tin ? `TIN ${p.tin}` : undefined,
            url: `/sales/clients/${p.id}`,
          });
      }
      if (can('purchases.vendor.view')) {
        const rows = await db.party.findMany({
          where: {
            companyId,
            isVendor: true,
            ...(own ? { ownerId: auth.userId } : {}),
            OR: [{ name: like }, { tin: like }],
          },
          take,
          orderBy: { name: 'asc' },
        });
        for (const p of rows)
          hits.push({
            type: 'Vendor',
            id: p.id,
            label: p.name,
            sub: p.tin ? `TIN ${p.tin}` : undefined,
            url: `/purchases/vendors/${p.id}`,
          });
      }
      if (can('crm.lead.view')) {
        const rows = await db.lead.findMany({
          where: {
            companyId,
            ...(own ? { ownerId: auth.userId } : {}),
            OR: [{ name: like }, { contactName: like }, { email: like }, { phone: like }],
          },
          take,
          orderBy: { id: 'desc' },
        });
        for (const l of rows)
          hits.push({
            type: 'Lead',
            id: l.id,
            label: l.name,
            sub: [l.contactName, l.status].filter(Boolean).join(' · '),
            url: `/crm/leads/${l.id}`,
          });
      }
      const docTypes = Object.keys(SALES_BASE).filter((t) => can(`sales.${t}.view`));
      if (docTypes.length) {
        const parties = await db.party.findMany({
          where: { companyId, name: like },
          select: { id: true },
          take: 20,
        });
        const rows = await db.salesDocument.findMany({
          where: {
            companyId,
            docType: { in: docTypes },
            ...(own ? { ownerId: auth.userId } : {}),
            OR: [
              { number: like },
              { reference: like },
              ...(parties.length ? [{ partyId: { in: parties.map((p) => p.id) } }] : []),
            ],
          },
          take,
          orderBy: { id: 'desc' },
        });
        for (const d of rows)
          hits.push({
            type: SALES_BASE[d.docType]![1],
            id: d.id,
            label: d.number ?? 'Draft',
            sub: d.reference ?? d.status,
            url: `${SALES_BASE[d.docType]![0]}/${d.id}`,
          });
      }
      if (can('sales.receipt.view')) {
        const rows = await db.paymentReceipt.findMany({
          where: {
            companyId,
            ...(own ? { createdBy: auth.userId } : {}),
            OR: [{ receiptNo: like }, { reference: like }],
          },
          take,
        });
        for (const r of rows)
          hits.push({
            type: 'Receipt',
            id: r.id,
            label: r.receiptNo,
            sub: r.reference ?? undefined,
            url: `/sales/receipts/${r.id}`,
          });
      }
      if (can('purchases.bill.view')) {
        const rows = await db.vendorBill.findMany({
          where: {
            companyId,
            ...(own ? { ownerId: auth.userId } : {}),
            OR: [{ number: like }, { vendorRef: like }],
          },
          take,
        });
        for (const b of rows)
          hits.push({
            type: 'Vendor bill',
            id: b.id,
            label: b.number ?? 'Draft bill',
            sub: b.vendorRef ?? undefined,
            url: `/purchases/bills/${b.id}`,
          });
      }
      if (can('purchases.order.view')) {
        const rows = await db.purchaseOrder.findMany({
          where: {
            companyId,
            ...(own ? { ownerId: auth.userId } : {}),
            OR: [{ number: like }, { reference: like }],
          },
          take,
        });
        for (const o of rows)
          hits.push({
            type: 'Purchase order',
            id: o.id,
            label: o.number ?? 'Draft order',
            sub: o.reference ?? undefined,
            url: `/purchases/orders/${o.id}`,
          });
      }
      if (can('purchases.expense.view')) {
        const rows = await db.expense.findMany({
          where: {
            companyId,
            ...(own ? { createdBy: auth.userId } : {}),
            OR: [{ number: like }, { description: like }],
          },
          take,
        });
        for (const e of rows)
          hits.push({
            type: 'Expense',
            id: e.id,
            label: e.number,
            sub: e.description,
            url: `/purchases/expenses/${e.id}`,
          });
      }
      if (can('purchases.payout.view')) {
        const rows = await db.payoutReceipt.findMany({
          where: {
            companyId,
            ...(own ? { createdBy: auth.userId } : {}),
            OR: [{ payoutNo: like }, { reference: like }],
          },
          take,
        });
        for (const p of rows)
          hits.push({
            type: 'Payout',
            id: p.id,
            label: p.payoutNo,
            sub: p.reference ?? undefined,
            url: `/purchases/payouts/${p.id}`,
          });
      }
      if (can('sales.item.view')) {
        const rows = await db.item.findMany({
          where: { companyId, OR: [{ name: like }, { sku: like }] },
          take,
        });
        for (const i of rows)
          hits.push({
            type: 'Item',
            id: i.id,
            label: i.name,
            sub: i.sku ?? undefined,
            url: '/sales/items',
          });
      }
      res.json({ items: hits.slice(0, 40) });
    }),
  );

  return router;
}
